import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import procurementDocumentsService from '../../../services/procurementDocumentsService';
import DocumentDownloadButtons from './DocumentDownloadButtons';
import CompetitiveAnalysisDocumentView from './CompetitiveAnalysisDocumentView';

const emptyVendorRow = () => ({
  name: '',
  unit_price: '',
  total_price: '',
  taxes_duties: '',
  freight: '',
  payment_terms: '',
  lead_time: '',
  warranty: '',
  quality_cert: '',
  past_performance_rating: '',
  compliance_specs: '',
  overall_score: '',
});
const emptySW = () => ({ vendor: '', strengths: '', weaknesses_risks: '' });

const CompetitiveAnalysisFormModal = ({ purchaseRequest, onClose }) => {
  const [form, setForm] = useState({
    analysis_date: new Date().toISOString().slice(0, 10),
    prepared_by: '',
    department: purchaseRequest.department || '',
    item_project_description: purchaseRequest.item_service_requested || '',
    recommended_vendor: '',
    estimated_savings: '',
    reviewed_by: '',
    approved_by: '',
  });
  const [vendors, setVendors] = useState([emptyVendorRow(), emptyVendorRow()]);
  const [strengthsWeaknesses, setStrengthsWeaknesses] = useState([emptySW()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [showView, setShowView] = useState(false);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const updateVendor = (index, field, value) => {
    setVendors((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const updateSW = (index, field, value) => {
    setStrengthsWeaknesses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    const realVendors = vendors.filter((v) => v.name.trim());
    if (realVendors.length < 2) {
      toast.error('Add at least 2 vendors to compare');
      return;
    }
    try {
      setSaving(true);
      const res = await procurementDocumentsService.createCompetitiveAnalysis({
        purchase_request_id: purchaseRequest.id,
        ...form,
        vendors_compared: realVendors,
        strengths_weaknesses: strengthsWeaknesses.filter((s) => s.vendor.trim()),
      });
      setSaved(res.data.data.analysis);
      toast.success('Competitive Analysis created');
    } catch (error) {
      console.error('Create competitive analysis error:', error);
      toast.error(error.response?.data?.message || 'Failed to create analysis');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Competitive Analysis - {purchaseRequest.request_id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {saved ? (
          <div className="text-center py-8">
            <p className="text-green-700 font-medium mb-1">Analysis {saved.reference_no} created</p>
            <p className="text-sm text-gray-500 mb-4">Download the document below.</p>
            <div className="flex justify-center">
              <DocumentDownloadButtons
                onView={() => setShowView(true)}
                onDownloadDocx={() => procurementDocumentsService.downloadCompetitiveAnalysisDocx(saved.id, `${saved.reference_no}.docx`)}
              />
            </div>
            <button onClick={onClose} className="mt-4 text-sm text-gray-500 hover:text-gray-700">
              Close
            </button>
            {showView && <CompetitiveAnalysisDocumentView analysis={saved} onClose={() => setShowView(false)} />}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Analysis Date</label>
                <input type="date" value={form.analysis_date} onChange={(e) => set('analysis_date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Prepared By</label>
                <input value={form.prepared_by} onChange={(e) => set('prepared_by', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                <input value={form.department} onChange={(e) => set('department', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="col-span-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Item / Project Description</label>
                <input value={form.item_project_description} onChange={(e) => set('item_project_description', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Vendor Quote Comparison (2-4 vendors)</h4>
                {vendors.length < 4 && (
                  <button onClick={() => setVendors((p) => [...p, emptyVendorRow()])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                    <Plus className="h-3.5 w-3.5" /> Add Vendor
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-1.5 text-left border">Criteria</th>
                      {vendors.map((_, i) => (
                        <th key={i} className="p-1.5 border">
                          Vendor {String.fromCharCode(65 + i)}
                          {vendors.length > 2 && (
                            <button onClick={() => setVendors((p) => p.filter((_, idx) => idx !== i))} className="ml-1 text-red-500">
                              <Trash2 className="h-3 w-3 inline" />
                            </button>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Company Name', 'name'],
                      ['Unit Price', 'unit_price'],
                      ['Total Quoted Price', 'total_price'],
                      ['Taxes / Duties', 'taxes_duties'],
                      ['Freight / Shipping', 'freight'],
                      ['Payment Terms', 'payment_terms'],
                      ['Lead Time / Delivery', 'lead_time'],
                      ['Warranty', 'warranty'],
                      ['Quality Certification', 'quality_cert'],
                      ['Past Performance Rating', 'past_performance_rating'],
                      ['Compliance with Specs', 'compliance_specs'],
                      ['Overall Score (1-10)', 'overall_score'],
                    ].map(([label, key]) => (
                      <tr key={key}>
                        <td className="p-1.5 border font-medium text-gray-600">{label}</td>
                        {vendors.map((v, i) => (
                          <td key={i} className="p-1 border">
                            <input value={v[key]} onChange={(e) => updateVendor(i, key, e.target.value)} className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Strengths & Weaknesses</h4>
                <button onClick={() => setStrengthsWeaknesses((p) => [...p, emptySW()])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus className="h-3.5 w-3.5" /> Add Row
                </button>
              </div>
              <div className="space-y-2">
                {strengthsWeaknesses.map((sw, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5">
                    <input placeholder="Vendor" value={sw.vendor} onChange={(e) => updateSW(i, 'vendor', e.target.value)} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Strengths" value={sw.strengths} onChange={(e) => updateSW(i, 'strengths', e.target.value)} className="col-span-4 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Weaknesses / Risks" value={sw.weaknesses_risks} onChange={(e) => updateSW(i, 'weaknesses_risks', e.target.value)} className="col-span-5 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <button onClick={() => setStrengthsWeaknesses((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 flex justify-center text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendation</h4>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Recommended Vendor" value={form.recommended_vendor} onChange={(e) => set('recommended_vendor', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Estimated Savings / Value" value={form.estimated_savings} onChange={(e) => set('estimated_savings', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Reviewed By" value={form.reviewed_by} onChange={(e) => set('reviewed_by', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Approved By" value={form.approved_by} onChange={(e) => set('approved_by', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Create Analysis'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitiveAnalysisFormModal;
